import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import BlockTypes from '../../../types/BlockTypes';
import withoutSelectionSnapshots from '../../utils/withoutSelectionSnapshots';
import getTableColumnCountForCells from '../utils/getTableColumnCountForCells';
import getTableRowCountForCells from '../utils/getTableRowCountForCells';

function canBeMerged(fragment) {
  return fragment.nodes.size === 1 && fragment.getBlocks().first().type === BlockTypes.TABLE_CELL || !fragment.getBlocks().find(function (node) {
    return node.type === BlockTypes.TABLE_CELL;
  });
}

function getFragmentColumnCount(fragment) {
  var cells = fragment.getBlocks();
  return cells.first().type === BlockTypes.TABLE_CELL ? getTableColumnCountForCells(cells) : 1;
}

function getFragmentRowCount(fragment) {
  var cells = fragment.getBlocks();
  return cells.first().type === BlockTypes.TABLE_CELL ? getTableRowCountForCells(cells) : cells.size;
}

function addMissingRowsAndColumns(editor, fragment, tableId, startRowIndex, startColumnIndex) {
  var tableCells = editor.getTableCells(tableId);
  var tableColumnCount = getTableColumnCountForCells(tableCells);
  var tableRowCount = getTableRowCountForCells(tableCells);
  var fragmentColumnCount = getFragmentColumnCount(fragment);
  var fragmentRowCount = getFragmentRowCount(fragment);
  var columnsToAdd = Math.max(0, fragmentColumnCount + startColumnIndex - tableColumnCount);
  var rowsToAdd = Math.max(0, fragmentRowCount + startRowIndex - tableRowCount);

  if (columnsToAdd > 0) {
    editor.addTableColumns(tableId, startColumnIndex + 1, columnsToAdd);
  }

  if (rowsToAdd > 0) {
    editor.addTableRows(tableId, startRowIndex + 1, rowsToAdd);
  }
}

function insertFragment(editor, fragment) {
  if (!editor.isSelectionInTable()) {
    throw new Error('Selection not in table');
  } else if (!canBeMerged(fragment)) {
    throw new Error('Fragment incompatible with a merge');
  }

  var tableId = editor.getCurrentTableId();
  var firstCell = editor.value.startBlock;

  var _editor$getCellCoords = editor.getCellCoords(firstCell),
      _editor$getCellCoords2 = _slicedToArray(_editor$getCellCoords, 2),
      startRowIndex = _editor$getCellCoords2[0],
      startColumnIndex = _editor$getCellCoords2[1];

  editor.snapshotSelection();
  return editor.withoutNormalizing(function () {
    addMissingRowsAndColumns(editor, fragment, tableId, startRowIndex, startColumnIndex); // const tableId = firstSelectedCell.getIn(['data', 'tableId'])

    var cells = editor.getTableCells(tableId);
    var startPath = editor.value.document.getPath(firstCell.key);
    var tableColumnCount = getTableColumnCountForCells(cells);
    var fragmentColumnCount = getFragmentColumnCount(fragment);
    var blocks = fragment.getBlocks();
    editor.insertFragment(blocks.first());

    if (blocks.skip(1).isEmpty()) {
      // Nothing left to do here
      return;
    }

    blocks.skip(1).reduce(function (editor, newCell, index) {
      var path = startPath.update(startPath.size - 1, function (firstIndex) {
        return firstIndex + (index + 1) % fragmentColumnCount + Math.floor((index + 1) / fragmentColumnCount) * tableColumnCount;
      });
      var oldCell = editor.value.document.getNode(path);
      return editor.replaceNodeByKey(oldCell.key, newCell.set('type', BlockTypes.TABLE_CELL).set('data', oldCell.data));
    }, editor);
    var lastText = fragment.getLastText();

    if (lastText) {
      editor.moveToEndOfNode(lastText);
    }
  });
}

export default function mergeCurrentTableWithFragment(editor, fullFragment) {
  editor.snapshotSelection();
  withoutSelectionSnapshots(editor, function () {
    var fragment = fullFragment.update('nodes', function (nodes) {
      return nodes.reverse().skipUntil(function (node) {
        return node.text !== '';
      }).reverse();
    });

    var _loop = function _loop() {
      var tableFragment = fragment.nodes.first().type === BlockTypes.TABLE ? fragment.update('nodes', function (nodes) {
        return nodes.take(1);
      }) : fragment.update('nodes', function (nodes) {
        return nodes.takeUntil(function (node) {
          return node.type === BlockTypes.TABLE;
        });
      });
      fragment = fragment.update('nodes', function (nodes) {
        return nodes.takeLast(nodes.size - tableFragment.nodes.size).reverse().skipUntil(function (node) {
          return node.text !== '';
        }).reverse();
      });
      insertFragment(editor, tableFragment);

      if (fragment.text !== '') {
        if (editor.isSelectionInLastTableRow()) {
          var tableId = editor.getCurrentTableId();
          editor.addTableRow(tableId).moveToRowBelow();
        } else {
          editor.moveToRowBelow();
        }
      }
    };

    while (fragment.text !== '') {
      _loop();
    }
  });
  return editor;
}